import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaGavel, FaArrowLeft, FaEdit, FaPhone, FaFax, FaEnvelope,
  FaMapMarkerAlt, FaGlobe, FaExternalLinkAlt, FaPlus, FaTrash,
  FaUser, FaBriefcase, FaSave, FaTimes, FaCheck
} from 'react-icons/fa';
import './CourtDetail.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const CourtDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [court, setCourt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editedCourt, setEditedCourt] = useState(null);
  const [showAddJudge, setShowAddJudge] = useState(false);
  const [showAddClerk, setShowAddClerk] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchCourtDetails();
  }, [id]);

  const fetchCourtDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/courts/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setCourt(data.data);
        setEditedCourt(data.data);
        setError('');
      } else {
        setError('Court not found');
      }
    } catch (err) {
      console.error('Error fetching court details:', err);
      setError('Failed to load court details');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCourtEdit = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/courts/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editedCourt)
      });

      const data = await response.json();

      if (data.success) {
        setCourt(data.data);
        setEditedCourt(data.data);
        setEditMode(false);
      } else {
        alert('Failed to update court');
      }
    } catch (err) {
      console.error('Error updating court:', err);
      alert('Failed to update court');
    }
  };

  const handleCancelEdit = () => {
    setEditedCourt(court);
    setEditMode(false);
  };

  const handleInputChange = (field, value) => {
    setEditedCourt(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getJurisdictionBadge = (jurisdiction) => {
    const badges = {
      'district': { label: 'District Court', color: '#2c3e50' },
      'justice': { label: 'Justice Court', color: '#16a085' },
      'municipal': { label: 'Municipal Court', color: '#2980b9' },
      'city': { label: 'City Court', color: '#8e44ad' },
      'other': { label: 'Other', color: '#7f8c8d' }
    };

    const badge = badges[jurisdiction] || badges.other;

    return (
      <span className="jurisdiction-badge" style={{ backgroundColor: badge.color }}>
        {badge.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="court-detail-container">
        <div className="loading">Loading court details...</div>
      </div>
    );
  }

  if (error || !court) {
    return (
      <div className="court-detail-container">
        <div className="error-message">{error || 'Court not found'}</div>
        <button className="btn-back" onClick={() => navigate('/address-book/courts')}>
          <FaArrowLeft /> Back to Courts
        </button>
      </div>
    );
  }

  return (
    <div className="court-detail-container">
      {/* Header */}
      <div className="page-header">
        <button className="btn-back" onClick={() => navigate('/address-book/courts')}>
          <FaArrowLeft /> Back to Courts
        </button>

        <div className="header-title">
          <FaGavel size={28} />
          <div>
            <h1>{court.name}</h1>
            <div className="header-badges">
              {getJurisdictionBadge(court.jurisdiction)}
              {court.court_identifier && (
                <span className="court-id-badge">ID: {court.court_identifier}</span>
              )}
            </div>
          </div>
        </div>

        {user.role === 'admin' && !editMode && (
          <button className="btn-edit-court" onClick={() => setEditMode(true)}>
            <FaEdit /> Edit Court Info
          </button>
        )}

        {editMode && (
          <div className="edit-actions">
            <button className="btn-save" onClick={handleSaveCourtEdit}>
              <FaSave /> Save
            </button>
            <button className="btn-cancel" onClick={handleCancelEdit}>
              <FaTimes /> Cancel
            </button>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="content-layout">
        {/* Court Information Section */}
        <div className="court-info-section">
          <h2>Court Information</h2>

          <div className="info-card-compact">
            <div className="compact-grid">
              <div className="compact-item">
                <label>Judicial District</label>
                {editMode ? (
                  <input
                    type="text"
                    value={editedCourt.judicial_district || ''}
                    onChange={(e) => handleInputChange('judicial_district', e.target.value)}
                  />
                ) : (
                  <span>{court.judicial_district || 'N/A'}</span>
                )}
              </div>

              <div className="compact-item">
                <label>Jurisdiction</label>
                {editMode ? (
                  <select
                    value={editedCourt.jurisdiction || ''}
                    onChange={(e) => handleInputChange('jurisdiction', e.target.value)}
                  >
                    <option value="">Select Jurisdiction</option>
                    <option value="district">District Court</option>
                    <option value="justice">Justice Court</option>
                    <option value="municipal">Municipal Court</option>
                    <option value="city">City Court</option>
                    <option value="other">Other</option>
                  </select>
                ) : (
                  <span>{court.jurisdiction ? court.jurisdiction.charAt(0).toUpperCase() + court.jurisdiction.slice(1) : 'N/A'}</span>
                )}
              </div>

              <div className="compact-item">
                <label>Court Identifier</label>
                {editMode ? (
                  <input
                    type="text"
                    value={editedCourt.court_identifier || ''}
                    onChange={(e) => handleInputChange('court_identifier', e.target.value)}
                  />
                ) : (
                  <span>{court.court_identifier || 'N/A'}</span>
                )}
              </div>

              <div className="compact-item">
                <label>County</label>
                {editMode ? (
                  <input
                    type="text"
                    value={editedCourt.county || ''}
                    onChange={(e) => handleInputChange('county', e.target.value)}
                  />
                ) : (
                  <span>{court.county || 'N/A'}</span>
                )}
              </div>

              <div className="compact-item full-width">
                <label>Address</label>
                {editMode ? (
                  <div className="address-edit-group">
                    <input
                      type="text"
                      placeholder="Street Address"
                      value={editedCourt.address_street || ''}
                      onChange={(e) => handleInputChange('address_street', e.target.value)}
                    />
                    <div className="address-row">
                      <input
                        type="text"
                        placeholder="City"
                        value={editedCourt.city || ''}
                        onChange={(e) => handleInputChange('city', e.target.value)}
                      />
                      <input
                        type="text"
                        placeholder="Zip"
                        value={editedCourt.address_zip || ''}
                        onChange={(e) => handleInputChange('address_zip', e.target.value)}
                        style={{ maxWidth: '120px' }}
                      />
                    </div>
                  </div>
                ) : (
                  <span>
                    {court.address_street || 'N/A'}
                    <br />
                    {court.city}, MT {court.address_zip}
                  </span>
                )}
              </div>

              <div className="compact-item">
                <label><FaPhone /> Phone</label>
                {editMode ? (
                  <input
                    type="text"
                    value={editedCourt.phone || ''}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                  />
                ) : (
                  <span>{court.phone || 'N/A'}</span>
                )}
              </div>

              <div className="compact-item">
                <label><FaFax /> Fax</label>
                {editMode ? (
                  <input
                    type="text"
                    value={editedCourt.fax || ''}
                    onChange={(e) => handleInputChange('fax', e.target.value)}
                  />
                ) : (
                  <span>{court.fax || 'N/A'}</span>
                )}
              </div>

              <div className="compact-item">
                <label><FaEnvelope /> Email</label>
                {editMode ? (
                  <input
                    type="email"
                    value={editedCourt.email || ''}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                  />
                ) : (
                  <span>
                    {court.email ? (
                      <a href={`mailto:${court.email}`}>{court.email}</a>
                    ) : 'N/A'}
                  </span>
                )}
              </div>

              <div className="compact-item full-width">
                <label><FaGlobe /> Website</label>
                {editMode ? (
                  <input
                    type="url"
                    value={editedCourt.website || ''}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                  />
                ) : (
                  <span>
                    {court.website ? (
                      <a href={court.website} target="_blank" rel="noopener noreferrer">
                        Visit Website <FaExternalLinkAlt size={10} />
                      </a>
                    ) : 'N/A'}
                  </span>
                )}
              </div>
            </div>

            {(editMode || court.notes) && (
              <>
                <div className="info-divider"></div>
                <div className="compact-item full-width">
                  <label>Notes</label>
                  {editMode ? (
                    <textarea
                      value={editedCourt.notes || ''}
                      onChange={(e) => handleInputChange('notes', e.target.value)}
                      rows="3"
                      placeholder="Add notes about this court..."
                    />
                  ) : (
                    <p className="notes-text">{court.notes}</p>
                  )}
                </div>
              </>
            )}
          </div>

          {/* External Resources */}
          <div className="external-resources">
            <h3>External Resources</h3>
            <div className="resource-links">
              <a
                href="https://courts.mt.gov/CourtLocator/"
                target="_blank"
                rel="noopener noreferrer"
                className="resource-link"
              >
                <FaGlobe />
                <div>
                  <strong>Montana Court Locator</strong>
                  <p>Find court locations and contact information</p>
                </div>
                <FaExternalLinkAlt />
              </a>
              <a
                href="https://dcportal.pubcourts.mt.gov/fullcourtweb/start.do"
                target="_blank"
                rel="noopener noreferrer"
                className="resource-link"
              >
                <FaGlobe />
                <div>
                  <strong>District Court Public Access</strong>
                  <p>Search district court cases and records</p>
                </div>
                <FaExternalLinkAlt />
              </a>
              <a
                href="https://coljportal.pubcourts.mt.gov/fullcourtweb/start.do"
                target="_blank"
                rel="noopener noreferrer"
                className="resource-link"
              >
                <FaGlobe />
                <div>
                  <strong>Lower Courts Public Access</strong>
                  <p>Search justice, municipal, and city court cases</p>
                </div>
                <FaExternalLinkAlt />
              </a>
            </div>
          </div>
        </div>

        {/* Court Staff Section */}
        <div className="court-staff-section">
          <h2>Court Staff</h2>

          {/* Judges */}
          <div className="staff-category">
            <div className="category-header">
              <h3><FaUser /> Judges ({court.judges?.length || 0})</h3>
              {user.role === 'admin' && (
                <button className="btn-add-staff" onClick={() => setShowAddJudge(!showAddJudge)}>
                  <FaPlus /> Add Judge
                </button>
              )}
            </div>

            {showAddJudge && (
              <AddJudgeForm
                courtId={id}
                onCancel={() => setShowAddJudge(false)}
                onSuccess={() => {
                  setShowAddJudge(false);
                  fetchCourtDetails();
                }}
              />
            )}

            <div className="staff-list">
              {court.judges?.length === 0 ? (
                <p className="no-staff">No judges added yet</p>
              ) : (
                court.judges?.map(judge => (
                  <StaffCard
                    key={judge.id}
                    staff={judge}
                    type="judge"
                    courtId={id}
                    onUpdate={fetchCourtDetails}
                    userRole={user.role}
                  />
                ))
              )}
            </div>
          </div>

          {/* Clerks */}
          <div className="staff-category">
            <div className="category-header">
              <h3><FaBriefcase /> Clerks ({court.clerks?.length || 0})</h3>
              {user.role === 'admin' && (
                <button className="btn-add-staff" onClick={() => setShowAddClerk(!showAddClerk)}>
                  <FaPlus /> Add Clerk
                </button>
              )}
            </div>

            {showAddClerk && (
              <AddClerkForm
                courtId={id}
                onCancel={() => setShowAddClerk(false)}
                onSuccess={() => {
                  setShowAddClerk(false);
                  fetchCourtDetails();
                }}
              />
            )}

            <div className="staff-list">
              {court.clerks?.length === 0 ? (
                <p className="no-staff">No clerks added yet</p>
              ) : (
                court.clerks?.map(clerk => (
                  <StaffCard
                    key={clerk.id}
                    staff={clerk}
                    type="clerk"
                    courtId={id}
                    onUpdate={fetchCourtDetails}
                    userRole={user.role}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Staff Card Component with individual edit capability
const StaffCard = ({ staff, type, courtId, onUpdate, userRole }) => {
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState(staff);

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = type === 'judge' ? 'judges' : 'clerks';
      const response = await fetch(`${API_URL}/courts/${courtId}/${endpoint}/${staff.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setEditing(false);
        onUpdate();
      }
    } catch (err) {
      console.error('Error updating staff:', err);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to remove this ${type}?`)) return;

    try {
      const token = localStorage.getItem('token');
      const endpoint = type === 'judge' ? 'judges' : 'clerks';
      await fetch(`${API_URL}/courts/${courtId}/${endpoint}/${staff.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      onUpdate();
    } catch (err) {
      console.error('Error deleting staff:', err);
    }
  };

  if (editing) {
    return (
      <div className="staff-card editing">
        <div className="edit-form">
          <div className="form-row">
            <div className="form-field">
              <label>Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="form-field">
              <label>Title</label>
              <input
                type="text"
                value={formData.title || ''}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <label>Phone</label>
              <input
                type="text"
                value={formData.phone || ''}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>
            <div className="form-field">
              <label>Extension</label>
              <input
                type="text"
                value={formData.phone_ext || ''}
                onChange={(e) => setFormData({ ...formData, phone_ext: e.target.value })}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <label>Email</label>
              <input
                type="email"
                value={formData.email || ''}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
            {type === 'clerk' && (
              <div className="form-field">
                <label>Fax</label>
                <input
                  type="text"
                  value={formData.fax || ''}
                  onChange={(e) => setFormData({ ...formData, fax: e.target.value })}
                />
              </div>
            )}
          </div>

          {type === 'clerk' && (
            <div className="form-field checkbox-field">
              <label>
                <input
                  type="checkbox"
                  checked={formData.is_primary || false}
                  onChange={(e) => setFormData({ ...formData, is_primary: e.target.checked })}
                />
                Primary Contact
              </label>
            </div>
          )}

          <div className="form-actions">
            <button className="btn-save-small" onClick={handleSave}>
              <FaCheck /> Save
            </button>
            <button className="btn-cancel-small" onClick={() => {
              setFormData(staff);
              setEditing(false);
            }}>
              <FaTimes /> Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`staff-card ${staff.is_primary ? 'primary' : ''}`}>
      <div className="staff-icon">
        {type === 'judge' ? <FaUser /> : <FaBriefcase />}
      </div>

      <div className="staff-info">
        <h4>
          {staff.name}
          {staff.is_primary && <span className="primary-badge">Primary</span>}
        </h4>
        {staff.title && <p className="staff-title">{staff.title}</p>}

        <div className="contact-details">
          {staff.phone && (
            <span><FaPhone /> {staff.phone}{staff.phone_ext ? ` ext. ${staff.phone_ext}` : ''}</span>
          )}
          {staff.fax && <span><FaFax /> {staff.fax}</span>}
          {staff.email && <span><FaEnvelope /> {staff.email}</span>}
        </div>
      </div>

      {userRole === 'admin' && (
        <div className="staff-actions">
          <button className="btn-edit-staff" onClick={() => setEditing(true)}>
            <FaEdit />
          </button>
          <button className="btn-delete-staff" onClick={handleDelete}>
            <FaTrash />
          </button>
        </div>
      )}
    </div>
  );
};

// Add Judge Form
const AddJudgeForm = ({ courtId, onCancel, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    title: 'Judge',
    phone: '',
    phone_ext: '',
    email: '',
    department: ''
  });

  const handleSubmit = async () => {
    if (!formData.name) {
      alert('Please enter a name');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/courts/${courtId}/judges`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        onSuccess();
      }
    } catch (err) {
      console.error('Error adding judge:', err);
    }
  };

  return (
    <div className="add-staff-form">
      <div className="form-row">
        <div className="form-field">
          <label>Name *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Enter judge name"
          />
        </div>
        <div className="form-field">
          <label>Title</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-field">
          <label>Phone</label>
          <input
            type="text"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          />
        </div>
        <div className="form-field">
          <label>Extension</label>
          <input
            type="text"
            value={formData.phone_ext}
            onChange={(e) => setFormData({ ...formData, phone_ext: e.target.value })}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-field">
          <label>Email</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          />
        </div>
        <div className="form-field">
          <label>Department</label>
          <input
            type="text"
            value={formData.department}
            onChange={(e) => setFormData({ ...formData, department: e.target.value })}
          />
        </div>
      </div>

      <div className="form-actions">
        <button className="btn-save-small" onClick={handleSubmit}>
          <FaPlus /> Add Judge
        </button>
        <button className="btn-cancel-small" onClick={onCancel}>
          <FaTimes /> Cancel
        </button>
      </div>
    </div>
  );
};

// Add Clerk Form
const AddClerkForm = ({ courtId, onCancel, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    title: 'Clerk',
    phone: '',
    phone_ext: '',
    fax: '',
    email: '',
    is_primary: false
  });

  const handleSubmit = async () => {
    if (!formData.name) {
      alert('Please enter a name');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/courts/${courtId}/clerks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        onSuccess();
      }
    } catch (err) {
      console.error('Error adding clerk:', err);
    }
  };

  return (
    <div className="add-staff-form">
      <div className="form-row">
        <div className="form-field">
          <label>Name *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Enter clerk name"
          />
        </div>
        <div className="form-field">
          <label>Title</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-field">
          <label>Phone</label>
          <input
            type="text"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          />
        </div>
        <div className="form-field">
          <label>Extension</label>
          <input
            type="text"
            value={formData.phone_ext}
            onChange={(e) => setFormData({ ...formData, phone_ext: e.target.value })}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-field">
          <label>Email</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          />
        </div>
        <div className="form-field">
          <label>Fax</label>
          <input
            type="text"
            value={formData.fax}
            onChange={(e) => setFormData({ ...formData, fax: e.target.value })}
          />
        </div>
      </div>

      <div className="form-field checkbox-field">
        <label>
          <input
            type="checkbox"
            checked={formData.is_primary}
            onChange={(e) => setFormData({ ...formData, is_primary: e.target.checked })}
          />
          Primary Contact
        </label>
      </div>

      <div className="form-actions">
        <button className="btn-save-small" onClick={handleSubmit}>
          <FaPlus /> Add Clerk
        </button>
        <button className="btn-cancel-small" onClick={onCancel}>
          <FaTimes /> Cancel
        </button>
      </div>
    </div>
  );
};

export default CourtDetail;
